import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../api.js";
import { useAuth } from "./AuthContext.jsx";

const ConnectivityContext = createContext(null);

const POLL_INTERVAL_MS = 15 * 1000;

// Single source of truth for "are we online" is the backend (sync.js's 30s connectivity tick) —
// this just polls the already-cheap local GET /api/sync/status rather than pinging central itself,
// so multiple open tabs/windows don't each hammer the central server independently.
export function ConnectivityProvider({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  const [recheckPending, setRecheckPending] = useState(false);
  const [syncPending, setSyncPending] = useState(false);
  const [lastManualSyncErrors, setLastManualSyncErrors] = useState([]);
  const wasOnlineRef = useRef(true);

  const refresh = useCallback(() => {
    api.syncStatus().then(s => {
      setStatus(s);
      if (wasOnlineRef.current && !s.online) setShowOfflineAlert(true);
      // Connectivity recovering on its own (the 30s backend tick, not a manual "Повторить")
      // should clear a stale alert too — otherwise it sits there forever once the network
      // blips back before anyone dismisses it.
      if (s.online) setShowOfflineAlert(false);
      wasOnlineRef.current = s.online;
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user || user.isCentral) return;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const sync = useCallback(async () => {
    setSyncPending(true);
    try {
      const r = await api.sync();
      setLastManualSyncErrors(r?.result?.errors || []);
      refresh();
      return r;
    } finally {
      setSyncPending(false);
    }
  }, [refresh]);

  const recheck = useCallback(async () => {
    setRecheckPending(true);
    try {
      const r = await api.recheckOnline();
      refresh();
      return r;
    } finally {
      setRecheckPending(false);
    }
  }, [refresh]);

  const value = {
    online: status?.online ?? true,
    onlineError: status?.onlineError ?? null,
    total: status?.total ?? 0,
    serverConfigured: status?.serverConfigured ?? false,
    lastSyncAt: status?.lastSyncAt ?? null,
    unsynced: status?.unsynced ?? { patients: 0, purchases: 0, documents: 0 },
    syncDetails: status?.details ?? [],
    syncErrors: lastManualSyncErrors.length ? lastManualSyncErrors : (status?.lastSyncErrors ?? []),
    recheckPending,
    syncPending,
    sync,
    recheck,
    refreshStatus: refresh,
    showOfflineAlert,
    dismissOfflineAlert: () => setShowOfflineAlert(false),
  };

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
}

export const useConnectivity = () => useContext(ConnectivityContext);
